import { LandingBlock } from '../types'

/** Miniatura por defecto del vídeo principal. */
export const AVA_VIDEO_THUMBNAIL =
  'https://cdn.magicpatterns.com/patterns/generated-images/bc84f3af-f91f-4ad3-b568-f3c34b103930.jpg'

/**
 * Textos compartidos por las dos plantillas de landing.
 * Se mantienen aquí para que ambas partan del mismo contenido y el usuario
 * pueda editarlo después bloque a bloque desde el constructor.
 */
export const AVA_COPY = {
  eyebrow: 'SISTEMA AVA',
  brandLine: 'ATRAE · FILTRA · CONVIERTE',
  title: 'En 15 días instalamos un sistema que atrae, filtra y convierte.',
  highlight: 'atrae, filtra y convierte',
  subtitle:
    'Contenido estratégico, publicidad e inteligencia artificial trabajando juntos para generar oportunidades de venta para tu empresa todos los días.',
  ctaLabel: 'Quiero implementar AVA',
  videoOverlay: 'Así funciona Sistema AVA.',
  videoNote: 'Descubre cómo funciona el Sistema AVA',
  formTitle: 'Cuéntanos sobre tu empresa',
  formSubtitle:
    'Responde unas preguntas y analizaremos si tu empresa puede beneficiarse del Sistema AVA antes de programar una reunión.',
  footerText: '© 2026 Sistema AVA. Atracción, Viralidad y Automatización.',
} as const

/** Sección de tres columnas: Atraemos · Filtramos · Convertimos. */
export const AVA_PILLARS = [
  {
    icon: 'Target',
    title: 'ATRAEMOS',
    text: 'Creamos contenido y campañas para atraer clientes potenciales interesados en tus productos o servicios.',
  },
  {
    icon: 'Bot',
    title: 'FILTRAMOS',
    text: 'Utilizamos automatizaciones e inteligencia artificial para filtrar y cualificar a los mejores prospectos.',
  },
  {
    icon: 'BarChart3',
    title: 'CONVERTIMOS',
    text: 'Convertimos esos prospectos en oportunidades de venta y los entregamos preparados para tu equipo comercial.',
  },
]

export type LandingTemplateId = 'con_video' | 'solo_formulario'

export interface LandingTemplate {
  id: LandingTemplateId
  name: string
  description: string
}

export const LANDING_TEMPLATES: LandingTemplate[] = [
  {
    id: 'con_video',
    name: 'Landing con vídeo',
    description:
      'Cabecera, titular, vídeo protagonista, Atraemos · Filtramos · Convertimos y formulario.',
  },
  {
    id: 'solo_formulario',
    name: 'Landing solo formulario',
    description: 'Cabecera, titular, subtítulo y formulario. Directa y enfocada a conversión.',
  },
]

/** Cabecera minimalista: logo a la izquierda y CTA a la derecha. */
function topbar(id: string): LandingBlock {
  return {
    id,
    type: 'topbar',
    visible: true,
    spacing: 'normal',
    config: {
      ctaLabel: AVA_COPY.ctaLabel,
      sticky: true,
      showLogo: true,
      logoHeight: 34,
    },
  }
}

function hero(id: string): LandingBlock {
  return {
    id,
    type: 'hero',
    visible: true,
    spacing: 'amplio',
    config: {
      showLogo: false,
      eyebrow: AVA_COPY.eyebrow,
      brandLine: AVA_COPY.brandLine,
      title: AVA_COPY.title,
      highlight: AVA_COPY.highlight,
      subtitle: AVA_COPY.subtitle,
      extraLine: '',
    },
  }
}

function video(id: string): LandingBlock {
  return {
    id,
    type: 'video',
    visible: true,
    spacing: 'normal',
    config: {
      url: '',
      thumbnailUrl: AVA_VIDEO_THUMBNAIL,
      overlayTitle: AVA_COPY.videoOverlay,
      note: AVA_COPY.videoNote,
      caption: '',
      ctaLabel: '',
    },
  }
}

function pillars(id: string): LandingBlock {
  return {
    id,
    type: 'servicios',
    visible: true,
    spacing: 'amplio',
    config: { title: '', subtitle: '', items: AVA_PILLARS },
  }
}

function formulario(id: string): LandingBlock {
  return {
    id,
    type: 'formulario',
    visible: true,
    spacing: 'amplio',
    config: {
      title: AVA_COPY.formTitle,
      subtitle: AVA_COPY.formSubtitle,
      showProgress: 'si',
      showPrivacy: 'si',
    },
  }
}

function footer(id: string): LandingBlock {
  return {
    id,
    type: 'footer',
    visible: true,
    spacing: 'normal',
    config: {
      text: AVA_COPY.footerText,
      links: ['Política de privacidad', 'Aviso legal', 'Cookies'],
    },
  }
}

/**
 * Devuelve los bloques de una plantilla. El generador de ids lo aporta quien
 * llama, para que en Supabase sean UUID válidos y en la plantilla estática
 * puedan ser ids legibles.
 */
export function templateBlocks(
  template: LandingTemplateId,
  makeId: () => string,
): LandingBlock[] {
  if (template === 'solo_formulario') {
    return [
      topbar(makeId()),
      hero(makeId()),
      formulario(makeId()),
      footer(makeId()),
    ]
  }
  return [
    topbar(makeId()),
    hero(makeId()),
    video(makeId()),
    pillars(makeId()),
    formulario(makeId()),
    footer(makeId()),
  ]
}
